import * as React from 'react'

import { Box, Dimensions } from '@factorialco/deprecated-design-system'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'

export type ExpensesListPageLayoutProps = {
  children: React.ReactNode
  /**
   * Padding for the layout container.
   * DataTable (deprecated) requires padding, DataCollection handles its own.
   */
  padding?: Dimensions
}

/**
 * Layout wrapper for ExpensesListPage that enables proper flexbox-based footer positioning.
 *
 * ## Why this pattern exists
 *
 * The `SyncFooter` component needs to be positioned at the bottom of the viewport,
 * not at the end of scrollable content. This requires a flex column layout with
 * proper height constraints.
 *
 * However, `ResponsivePage.Body`'s internal `Container` component has `overflow: auto`
 * without a height constraint, which breaks the CSS height chain. When wrapped by
 * `ResponsivePage.Body`, our `height: 100%` becomes 100% of "auto" (content height),
 * making the flexbox layout ineffective.
 *
 * ## The solution
 *
 * We bypass `ResponsivePage.Body` entirely and use our own Box with proper height
 * constraints. We wrap with ErrorBoundary for error handling. The `data-scroll-container`
 * attribute is required for InfiniteScroll components to detect the scroll parent.
 *
 * Note: We cannot render a hidden `ResponsivePage.Body` as it would create a competing
 * `data-scroll-container` that InfiniteScroll would attach to instead of our visible one.
 *
 * TODO: Adjust it to fit only DataCollection needs after migration from DataTable
 *
 */

export const ExpensesListPageLayout = ({ children, padding }: ExpensesListPageLayoutProps) => {
  return (
    <ErrorBoundary blankSlate>
      <Box padding={padding} height="full" style={{ overflow: 'auto' }} data-scroll-container>
        {children}
      </Box>
    </ErrorBoundary>
  )
}
